/**
 * Line icons, drawn to match the pencil sketch the interface was designed from:
 * open outlines, one stroke weight, everything inheriting `currentColor` so a
 * button's own state colours its glyph.
 */

import type { JSX, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

/** Shared frame: a 24-unit square with round caps and no fill. */
function Icon({ size = 24, children, ...rest }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const PlayIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <path d="M7 4.5 19.5 12 7 19.5Z" />
  </Icon>
);

export const PauseIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <path d="M8.5 4.5v15M15.5 4.5v15" />
  </Icon>
);

export const StopIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <rect x="5.5" y="5.5" width="13" height="13" rx="1.5" />
  </Icon>
);

/** Record — filled, because it is the one control that captures rather than plays. */
export const RecordIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="5.2" fill="currentColor" stroke="none" />
  </Icon>
);

/** A metronome: the tap-tempo control. */
export const MetronomeIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <path d="M9.4 4.5h5.2l3.1 15H6.3Z" />
    <path d="M7.6 14.2h8.8" />
    <path d="M15.6 6.6 9.2 16.4" />
  </Icon>
);

export const UndoIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <path d="M8.5 7.5 5 11l3.5 3.5" />
    <path d="M5 11h8.2a5 5 0 0 1 0 10H9" />
  </Icon>
);

export const RedoIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <path d="M15.5 7.5 19 11l-3.5 3.5" />
    <path d="M19 11h-8.2a5 5 0 0 0 0 10H15" />
  </Icon>
);

/** A die: a fresh exercise, at the cost of the current one. */
export const DiceIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <rect x="4" y="4" width="16" height="16" rx="3.5" />
    <circle cx="8.6" cy="8.6" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="15.4" cy="8.6" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="8.6" cy="15.4" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="15.4" cy="15.4" r="1.15" fill="currentColor" stroke="none" />
  </Icon>
);

export const HelpIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.6 9.6a2.5 2.5 0 1 1 3.2 2.4c-.6.2-.8.7-.8 1.3v.5" />
    <path d="M12 16.6h.01" strokeWidth={2.2} />
  </Icon>
);

/** A spanner: miscellaneous settings. */
export const SettingsIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <path d="M15.4 4.6a4.2 4.2 0 0 0-5.1 5.4l-5.6 5.6a1.8 1.8 0 0 0 2.5 2.5l5.6-5.6a4.2 4.2 0 0 0 5.4-5.1l-2.6 2.6-2.3-.6-.6-2.3Z" />
  </Icon>
);

/** The grade arrow — the one control drawn in the student's own colour. */
export const GradeArrowIcon = (p: IconProps): JSX.Element => (
  <Icon {...p} strokeWidth={3}>
    <path d="M3 12h16" />
    <path d="M13.5 5.8 20.5 12l-7 6.2" />
  </Icon>
);

export const CloseIcon = (p: IconProps): JSX.Element => (
  <Icon {...p}>
    <path d="M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5" />
  </Icon>
);

/**
 * The ginkgo leaf of the masthead — a fan on a slender stalk, and the app's one
 * piece of colour when nothing is happening.
 */
export function LeafMark({ size = 38 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size * 0.76} viewBox="0 0 46 35" aria-hidden="true" focusable="false">
      <path
        d="M9.5 27.4C4 24.6 5.4 16 12 10.2 18 5 26.4 2.6 32.6 4.2c4 1 5.4 4 3.6 6.4-1.3 1.8-3.3 2.4-5 2.2 3 1.4 5 4.7 4 8.4-1.6 6-11.5 9.4-19.4 8-2.4-.4-4.4-1-6.3-1.8Z"
        fill="var(--accent)"
      />
      <path
        d="M9.8 27.6C7 29.6 4.4 30.6 1.8 30.9"
        stroke="var(--accent)"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M12.5 25.6c1.6-4.4 4.6-9 8.6-12.6"
        stroke="var(--bg)"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
      />
    </svg>
  );
}
